import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError, timer } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, tap, retry, delayWhen } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private baseUrl = `${environment.apiUrl}${environment.apiEndpoint}/auth`;
  private tokenExpiredSubject = new BehaviorSubject<boolean>(false);
  public tokenExpired$ = this.tokenExpiredSubject.asObservable();
  
  // Observable pour les changements d'état d'authentification
  private authStatusSubject = new BehaviorSubject<boolean>(this.isAuthenticated());
  public authStatus$ = this.authStatusSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  login(form: { username: string; password: string }): Observable<any> {
    const body = new URLSearchParams();
    body.set('username', form.username);
    body.set('password', form.password);

    return this.http.post(`${this.baseUrl}/login`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'json'
    }).pipe(
      // Retry automatique pour les erreurs 500 (serveur temporairement indisponible)
      retry({
        count: 1, // Reessayer 1 fois
        delay: (error, retryCount) => {
          // Ne reessayer que pour les erreurs serveur 500
          if (error.status === 500 || error.status === 0) {
            console.log(`Tentative de reconnexion (${retryCount}/1)...`);
            return timer(1500); // Attendre 1.5 secondes avant de reessayer
          }
          // Pour les autres erreurs (401, 403, 404...), ne pas reessayer
          return throwError(() => error);
        }
      }),
      tap((response: any) => {
        if (response.access_token) {
          this.setToken(response.access_token);
          this.tokenExpiredSubject.next(false);
          this.authStatusSubject.next(true);
          
          // Stocker les infos utilisateur si disponibles
          const userObj = response.user || response;
          if (userObj && userObj.email) {
            localStorage.setItem('username', userObj.username || userObj.email || '');
            localStorage.setItem('email', userObj.email || '');
            localStorage.setItem('role', userObj.role || 'USER');
          }
          
          // Extraire l'ID du token JWT
          try {
            const token = response.access_token;
            if (token) {
              const payload = JSON.parse(atob(token.split('.')[1]));
              if (payload.user_id) {
                localStorage.setItem('user_id', payload.user_id.toString());
              }
            }
          } catch (e) {
            console.error('Failed to parse JWT token in auth service', e);
          }
        }
      }),
      catchError(error => {
        console.error('Login error:', error);
        
        // Detecter si le serveur a renvoye du HTML au lieu de JSON
        if (error.error && typeof error.error === 'string' && error.error.includes('<!DOCTYPE')) {
          // Creer une erreur plus claire pour l'utilisateur
          const customError = {
            status: error.status || 500,
            error: {
              detail: 'Le serveur rencontre un probleme temporaire. Veuillez reessayer dans quelques instants.'
            }
          };
          return throwError(() => customError);
        }
        
        return throwError(() => error);
      })
    );
  }

  register(user: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/register`, user);
  }

  logout(): Observable<any> {
    return this.http.post(`${this.baseUrl}/logout`, {}).pipe(
      tap(() => {
        this.clearToken();
      })
    );
  }

  verifyToken(): Observable<any> {
    return this.http.get(`${this.baseUrl}/verify-token`).pipe(
      catchError(error => {
        if (error.status === 401) {
          this.handleTokenExpired();
        }
        return throwError(() => error);
      })
    );
  }

  handleTokenExpired(): void {
    console.log('Token expire - Affichage du modal de reconnexion');
    this.clearToken();
    this.tokenExpiredSubject.next(true);
  }

  clearTokenExpired(): void {
    this.tokenExpiredSubject.next(false);
  }

  reconnect(): void {
    console.log('Reconnexion demandee - Redirection vers login');
    this.clearTokenExpired();
    this.router.navigate(['/login']);
  }

  setToken(token: string): void {
    localStorage.setItem('access_token', token);
    this.authStatusSubject.next(true);
  }

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  clearToken(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('role');
    this.authStatusSubject.next(false);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  isAdmin(): boolean {
    const role = localStorage.getItem('role');
    return role === 'ADMIN' || role === 'admin';
  }

  isTokenExpired(): boolean {
    return this.tokenExpiredSubject.value;
  }

  /**
   * Verifie si le token JWT est valide (non expire)
   * Decode le token et verifie la date d'expiration
   */
  isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      // Decoder le payload du JWT (partie centrale apres le premier '.')
      const payload = token.split('.')[1];
      if (!payload) {
        return false;
      }

      // Decoder le base64
      const decodedPayload = JSON.parse(atob(payload));
      
      // Verifier si le token a une date d'expiration
      if (!decodedPayload.exp) {
        return false;
      }

      // Comparer avec la date actuelle (exp est en secondes, Date.now() en millisecondes)
      const expirationDate = decodedPayload.exp * 1000;
      const now = Date.now();
      
      // Ajouter une marge de 30 secondes pour eviter les problemes de synchronisation
      const isValid = expirationDate > (now + 30000);
      
      if (!isValid) {
        console.log('Token expire - Expiration:', new Date(expirationDate), 'Maintenant:', new Date(now));
        // Nettoyer le token expire
        this.clearToken();
      }
      
      return isValid;
    } catch (error) {
      console.error('Erreur lors du decodage du token:', error);
      this.clearToken();
      return false;
    }
  }
}

